import type { ReactNode, Ref } from 'react';

import { cn } from '../lib/utils';

import { ChipContext } from './chip-context';
import { type ChipColor, type ChipSize, colorSurface, containerSize, radius } from './tokens';

type Props = {
  /** Tile dimensions and radius: `sm` 14×14 / 6px, `md` 20×20 / 8px (default),
   *  `lg` 32×32 / 12px.
   *  @default 'md' */
  size?: ChipSize;
  /** Surface tint. `primary` on neutral surfaces (lists, headers),
   *  `highlight` on tinted/branded sections.
   *  @default 'primary' */
  color?: ChipColor;
  /** Tile content. Use the DS primitives `<Avatar>`, `<Flag>`, or
   *  `<Glyph>` — they consume the chip's size via context. */
  children: ReactNode;
  className?: string;
  ref?: Ref<HTMLSpanElement>;
};

/** Square tile used as the visual home for an entity representation:
 *  contact (`<Avatar>`), line (`<Flag>`), template (`<Glyph>`), etc.
 *  The chip owns the tile chrome (size, radius, surface, border) and is
 *  decorative (`aria-hidden`) — pair with a visible label. */
export const Chip = ({ size = 'md', color = 'primary', children, className, ref }: Props) => (
  <ChipContext.Provider value={size}>
    <span
      ref={ref}
      aria-hidden
      data-slot="chip"
      className={cn(
        'inline-flex shrink-0 items-center justify-center overflow-hidden',
        containerSize[size],
        radius[size],
        colorSurface[color],
        className,
      )}
    >
      {children}
    </span>
  </ChipContext.Provider>
);

export type { Props as ChipProps };
